import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { useEffect } from 'react';

import { MarkdownMedia } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import { RiskBadge } from '@/components/RiskBadge';
import { useActionHotkey } from '@/hooks/hotkeys';
import { getTypeBadgeLabel } from '@/sections/webpages/utils/badges';
import { Webpage } from '@/types';
import { cn } from '@/utils/classname';

export interface FocusReviewProps {
  /** The full ordered set of captures (already sorted/filtered by the host). */
  webpages: Webpage[];
  /** Controlled active index (the host's shared `activeIndex`). */
  index: number;
  open: boolean;
  onClose: () => void;
  /** ←/→ and filmstrip clicks request a new absolute index. */
  onIndexChange: (next: number) => void;
  /**
   * Host-owned live-site open. The host owns the single validated new-tab sink
   * (protocol allowlist + `noopener,noreferrer`, security R1/R2) — this
   * component MUST NOT open new windows/tabs itself.
   */
  onOpenLiveSite: (webpage: Webpage) => void;
  markViewed: (key: string) => void;
  isViewed: (key: string) => boolean;
}

/**
 * Focus Review — the in-app "enlarge" lightbox (ENG-4341 Option C). A
 * feature-local composition of the canonical `Modal` (Radix Dialog → focus-trap,
 * Esc-to-close, focus-restore for free) plus `useActionHotkey` for ←/→/V/O,
 * mirroring `ImageModal`'s a11y architecture and adding risk/viewed metadata and
 * a filmstrip. The screenshot renders as an in-app `<img>` via `MarkdownMedia`
 * (security R4/R5) — no live content is framed and the presigned URL is never
 * copied/linked (R6/R8).
 */
export function FocusReview({
  webpages,
  index,
  open,
  onClose,
  onIndexChange,
  onOpenLiveSite,
  markViewed,
  isViewed,
}: FocusReviewProps) {
  const count = webpages.length;
  const activeWebpage = webpages[index];
  const activeKey = activeWebpage?.key;

  // Mark viewed on open and on every navigate. `markViewed` is idempotent
  // (dedupes, returns the same array reference when the key is already present),
  // so this effect cannot loop even though it is keyed on the active key.
  useEffect(() => {
    if (open && activeKey) markViewed(activeKey);
  }, [open, activeKey, markViewed]);

  const goPrev = () => {
    if (count > 0) onIndexChange((index - 1 + count) % count);
  };
  const goNext = () => {
    if (count > 0) onIndexChange((index + 1) % count);
  };

  // Esc is handled natively by the Radix `Modal`. `scope: 'overlay'` keeps these
  // out of the global bus, and `enabled: open` makes them inert when closed.
  useActionHotkey('left', goPrev, { enabled: open, scope: 'overlay' });
  useActionHotkey('right', goNext, { enabled: open, scope: 'overlay' });
  useActionHotkey('v', () => activeKey && markViewed(activeKey), {
    enabled: open,
    scope: 'overlay',
  });
  useActionHotkey('o', () => activeWebpage && onOpenLiveSite(activeWebpage), {
    enabled: open,
    scope: 'overlay',
  });

  const focusRing =
    'focus-visible:ring-2 focus-visible:ring-chariot-brand-primary focus:outline-hidden';

  // The score comes straight off the Webpage node (ENG-4395). An absent
  // riskScore (never cron-stamped) renders the neutral "—" chip.
  const activeScore = activeWebpage?.riskScore;
  const activeViewed = activeKey ? isViewed(activeKey) : false;
  const typeLabel = activeWebpage
    ? getTypeBadgeLabel(activeWebpage.type)
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="7xl"
      title={
        <span className="text-chariot-text-primary block truncate font-mono text-base">
          {activeWebpage?.url ?? 'Screenshot'}
        </span>
      }
      hideFooter
      closeOnOutsideClick
    >
      {activeWebpage && (
        <div className="flex flex-col gap-3">
          {/* Header: risk + metadata + counter + explicit Open live site */}
          <div className="flex flex-wrap items-center gap-3">
            <RiskBadge score={activeScore} />
            {typeLabel && (
              <span className="bg-chariot-background-secondary text-chariot-text-secondary rounded-full px-2 py-0.5 text-xs font-medium">
                {typeLabel}
              </span>
            )}
            {activeWebpage.service && (
              <span className="bg-chariot-background-secondary text-chariot-text-secondary rounded-full px-2 py-0.5 text-xs font-medium capitalize">
                {activeWebpage.service}
              </span>
            )}
            {activeViewed && (
              <span className="text-chariot-text-secondary flex items-center gap-1 text-xs">
                <EyeIcon className="size-3.5" />
                Viewed
              </span>
            )}
            <div className="ml-auto flex items-center gap-3">
              <div
                aria-live="polite"
                className="text-chariot-text-secondary text-xs tabular-nums"
              >
                {`${index + 1} of ${count}`}
              </div>
              <button
                type="button"
                onClick={() => onOpenLiveSite(activeWebpage)}
                aria-label="Open live site in new tab"
                className={cn(
                  'border-chariot-stroke text-chariot-text-primary hover:bg-chariot-background-secondary flex min-h-11 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium',
                  focusRing
                )}
              >
                <ArrowTopRightOnSquareIcon className="size-4" />
                Open live site
              </button>
            </div>
          </div>

          {/* Large in-app capture with prev/next chevrons */}
          <div className="bg-chariot-background-secondary relative flex min-h-[400px] items-center justify-center overflow-hidden rounded-md">
            {activeWebpage.screenshot ? (
              <MarkdownMedia src={activeWebpage.screenshot} thumbnail={false} />
            ) : (
              <div className="text-chariot-text-secondary flex flex-col items-center gap-1">
                <NoSymbolIcon className="size-10" />
                <span className="text-xs">Screenshot unavailable</span>
              </div>
            )}
            {count > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous capture"
                  className={cn(
                    'bg-chariot-background-primary hover:bg-chariot-background-secondary absolute top-1/2 left-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full shadow-md',
                    focusRing
                  )}
                >
                  <ChevronLeftIcon className="size-6" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next capture"
                  className={cn(
                    'bg-chariot-background-primary hover:bg-chariot-background-secondary absolute top-1/2 right-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full shadow-md',
                    focusRing
                  )}
                >
                  <ChevronRightIcon className="size-6" />
                </button>
              </>
            )}
          </div>

          {/* Filmstrip */}
          <div
            role="listbox"
            aria-label="Screenshot filmstrip"
            className="flex gap-2 overflow-x-auto rounded-md p-2 shadow-md"
          >
            {webpages.map((webpage, i) => {
              const isActive = i === index;
              return (
                <button
                  key={webpage.key}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  aria-current={isActive ? 'true' : undefined}
                  aria-label={webpage.url}
                  onClick={() => onIndexChange(i)}
                  className={cn(
                    'relative h-16 w-24 shrink-0 overflow-hidden rounded-md border',
                    isActive
                      ? 'border-chariot-brand-primary ring-chariot-brand-primary ring-2 motion-safe:scale-105'
                      : 'border-chariot-stroke hover:bg-chariot-background-secondary',
                    'motion-safe:transition-transform',
                    focusRing
                  )}
                >
                  {webpage.screenshot ? (
                    <MarkdownMedia src={webpage.screenshot} thumbnail={false} />
                  ) : (
                    <NoSymbolIcon className="text-chariot-text-disabled m-auto size-6" />
                  )}
                  {isViewed(webpage.key) && (
                    <EyeIcon className="text-chariot-text-disabled bg-chariot-background-primary/80 absolute top-0.5 right-0.5 size-3.5 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}
